'use client';

import { useState, useRef, useEffect } from 'react';
import { HistoryItem } from '@/lib/types';
import api from '@/lib/api';

interface HistoryPanelProps {
  history: HistoryItem[];
  onReuseParams: (item: HistoryItem) => void;
  onDeleteItem: (itemId: number) => void;
  onClearHistory: (period?: 'today' | 'yesterday' | 'all') => void;
}

export default function HistoryPanel({ history, onReuseParams, onDeleteItem, onClearHistory }: HistoryPanelProps) {
  const [showClearMenu, setShowClearMenu] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isFocused, setIsFocused] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowClearMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!isFocused || history.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in an input field
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(history.length - 1, prev + 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && history[selectedIndex]) {
            onReuseParams(history[selectedIndex]);
          }
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          if (selectedIndex >= 0 && history[selectedIndex]) {
            onDeleteItem(history[selectedIndex].id);
            // Adjust selection after delete
            if (selectedIndex >= history.length - 1) {
              setSelectedIndex(Math.max(0, history.length - 2));
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setSelectedIndex(-1);
          panelRef.current?.blur();
          setIsFocused(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFocused, history, selectedIndex, onReuseParams, onDeleteItem]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);
  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div
      ref={panelRef}
      tabIndex={0}
      onFocus={() => { setIsFocused(true); if (selectedIndex === -1 && history.length > 0) setSelectedIndex(0); }}
      onBlur={() => setIsFocused(false)}
      className="h-full flex flex-col bg-[var(--bg-surface)] outline-none"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--border-subtle)]">
        <span className="text-sm font-medium text-[var(--text-secondary)]" title="↑↓ Navigate • Enter Reuse • Del Remove">History</span>
        {history.length > 0 && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowClearMenu(!showClearMenu)}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-subtle cursor-pointer flex items-center gap-1"
            >
              Clear
              <svg className={`w-3 h-3 transition-transform ${showClearMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {showClearMenu && (
              <div className="absolute right-0 top-full mt-1 w-32 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg shadow-lg overflow-hidden z-20">
                <button
                  onClick={() => { onClearHistory('today'); setShowClearMenu(false); }}
                  className="w-full px-3 py-2 text-xs text-left text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-subtle cursor-pointer"
                >
                  Today
                </button>
                <button
                  onClick={() => { onClearHistory('yesterday'); setShowClearMenu(false); }}
                  className="w-full px-3 py-2 text-xs text-left text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-subtle cursor-pointer"
                >
                  Yesterday
                </button>
                <div className="h-px bg-[var(--border-subtle)]" />
                <button
                  onClick={() => { onClearHistory('all'); setShowClearMenu(false); }}
                  className="w-full px-3 py-2 text-xs text-left text-[var(--error)] hover:bg-[var(--error-soft)] transition-subtle cursor-pointer"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {history.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-[var(--text-muted)]">
            No history yet
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {history.map((item, index) => (
              <div
                key={item.id}
                ref={el => { itemRefs.current[index] = el; }}
                onClick={() => setSelectedIndex(index)}
                className={`group relative rounded-lg transition-subtle cursor-pointer ${
                  selectedIndex === index && isFocused
                    ? 'bg-[var(--accent-soft)] ring-1 ring-[var(--accent)]'
                    : 'hover:bg-[var(--bg-hover)]'
                }`}
              >
                {/* Delete button */}
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteItem(item.id); }}
                  className="absolute top-2 right-2 z-10 p-1 rounded bg-[var(--bg-base)]/80 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 hover:bg-[var(--error)] hover:text-white transition-all cursor-pointer"
                  title="Delete"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <button
                  onClick={() => onReuseParams(item)}
                  className="w-full p-2 flex gap-3 text-left cursor-pointer"
                >
                  {/* Thumbnail - show batch icon for batch entries */}
                  {item.job_type === 'batch' ? (
                    <div className="w-12 h-12 rounded-md flex-shrink-0 bg-[var(--accent-soft)] flex items-center justify-center relative">
                      <svg className="w-5 h-5 text-[var(--accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                      </svg>
                      {item.params.total_jobs && (
                        <span className="absolute -top-1 -right-1 bg-[var(--accent)] text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                          {item.params.total_jobs}
                        </span>
                      )}
                    </div>
                  ) : item.thumbnail ? (
                    <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-[var(--bg-elevated)]">
                      <img
                        src={api.getThumbnailUrl(item.thumbnail)}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-md flex-shrink-0 bg-[var(--bg-elevated)] flex items-center justify-center">
                      <svg className="w-5 h-5 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-xs text-[var(--text-secondary)] line-clamp-2 leading-snug">
                      {item.prompt}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                      {item.job_type === 'batch' && (
                        <>
                          <span className="text-[var(--accent)] font-medium">Batch</span>
                          <span className="text-[var(--border-default)]">|</span>
                        </>
                      )}
                      <span>{item.params.width}x{item.params.height}</span>
                      <span className="text-[var(--border-default)]">|</span>
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
